// Re-use the single source of truth for the Title Search step-status domain
// (pending | in_progress | completed | not_found | failed | blocked).
import type { TitleSearchStepStatus } from '../titleSearch/polling';

// ============================================
// Job Types
// ============================================

/**
 * JobType identifies which feature a job belongs to.
 * DD = Due Diligence, BA = Borrower Analysis, TR = Title Report (Title Search)
 */
export enum JobType {
  DD = 'DD',
  BA = 'BA',
  TR = 'TR'
}

/**
 * Job status - overall state of the job
 */
export enum JobStatus {
  DOCUMENT_PROCESSING = 'DOCUMENT_PROCESSING',
  DOCUMENT_VERIFICATION = 'DOCUMENT_VERIFICATION',
  INVESTOR_GUIDELINE_REVIEW = 'INVESTOR_GUIDELINE_REVIEW',
  UNDERWRITING_GUIDELINE_REVIEW = 'UNDERWRITING_GUIDELINE_REVIEW',
  COMPLETED = 'COMPLETED',
  FAILED = 'FAILED',
  ARCHIVED = 'ARCHIVED',
}

/**
 * Step status - state of each individual step
 */
export enum StepStatus {
  NOT_STARTED = 'NOT_STARTED',
  IN_PROGRESS = 'IN_PROGRESS',
  COMPLETED = 'COMPLETED',
  FAILED = 'FAILED'
}

/**
 * Step type - identifies each step in the workflow
 */
export enum StepType {
  DOCUMENT_PROCESSING = 'DOCUMENT_PROCESSING',
  DOCUMENT_VERIFICATION = 'DOCUMENT_VERIFICATION',
  INVESTOR_GUIDELINE_REVIEW = 'INVESTOR_GUIDELINE_REVIEW',
  /** Title Search only: the Findings step. */
  FINDINGS_DISPLAY = 'FINDINGS_DISPLAY',
}

/**
 * Job - unified job entity for both Due Diligence and Borrower Analysis
 */
export interface Job {
  jobId: string;
  name: string;
  tenantId: string;
  username: string;
  cognitoSub: string;
  jobType: JobType;
  status: JobStatus;

  // Step statuses
  documentProcessingStatus?: StepStatus;
  documentVerificationStatus?: StepStatus;
  investorGuidelineReviewStatus?: StepStatus;

  // Title Search (jobType === 'TR') step statuses.
  // These use the title-search step-status domain
  // (pending | in_progress | completed | not_found | failed | blocked)
  // and are populated only for TR jobs. See Requirement 8.2.
  propertyTaxSearchStatus?: TitleSearchStepStatus | null;
  recordedDocumentSearchStatus?: TitleSearchStepStatus | null;
  findingsDisplayStatus?: TitleSearchStepStatus | null;

  // Title Search (jobType === 'TR') creation fields and findings. Nullable on
  // the shared Job entity; populated only for TR jobs (Requirements 9.5, 9.6).
  loanNumber?: string | null;
  clientName?: string | null;
  borrowerName?: string | null;
  effectiveDate?: string | null; // ISO 8601 (AWSDate) calendar date
  propertyAddress?: string | null;
  propertyCounty?: string | null;
  propertyState?: string | null;
  propertyTaxInformation?: PropertyTaxInformation | null;
  deedInformation?: DeedRecord[] | null;

  // Title Analysis fields (TR jobs only; Requirements 7.13, 18.3).
  useCase?: string | null;
  vestingEntity?: string | null;
  guarantorNames?: string | null;
  subjectLoanAmount?: number | null;
  noteDate?: string | null;
  searchStartDate?: string | null;
  indexesSearched?: string[] | null;
  subjectMortgageRecordingReference?: string | null;
  expectedLienPosition?: string | null;
  currentLoanHolder?: string | null;
  apn?: string | null;
  /** pending | in_progress | completed | failed | blocked */
  titleAnalysisStatus?: string | null;
  titleAnalysisError?: string | null;

  // Progress metrics
  loanCount: number;
  processedLoanCount: number;
  failedLoanCount?: number;
  verifiedDocumentCount?: number;
  totalDocumentCount?: number;
  guidelineCheckCount?: number;
  totalGuidelineCount?: number;

  // Locations (optional for backward compatibility)
  uploadLocation?: string;
  exportLocation?: string;

  // Timestamps
  createdAt: string;
  startedAt?: string;
  completedAt?: string;
}

// Backward compatibility alias
export type WorkflowJob = Job;
export type WorkflowJobStatus = JobStatus;

/**
 * JobConnection - paginated list response
 */
export interface JobConnection {
  items: Job[];
  nextToken: string | null;
}

// Backward compatibility alias
export type WorkflowJobConnection = JobConnection;

// ============================================
// Job Type Configuration
// ============================================

/**
 * Job-type-specific configuration for labels and descriptions
 */
export interface JobTypeConfig {
  jobType: JobType;
  displayName: string;
  description: string;
  landingPageTitle: string;
  createJobButtonLabel: string;
  stepLabels: {
    documentProcessing: string;
    documentVerification: string;
    investorGuidelineReview: string;
  };
  /**
   * Title-search step labels, present only for the `TR` job type. The
   * title-search workflow has its own four ordered steps (Property Tax Search,
   * Recorded Document Search, Document Processing, Findings Display) that differ
   * from the DD/BA three-step model, so they are carried separately here rather
   * than overloading `stepLabels`. See Requirement 8.1.
   */
  titleSearchStepLabels?: {
    propertyTaxSearch: string;
    recordedDocumentSearch: string;
    documentProcessing: string;
    findingsDisplay: string;
  };
}

export const JOB_TYPE_CONFIGS: Record<JobType, JobTypeConfig> = {
  [JobType.DD]: {
    jobType: JobType.DD,
    displayName: 'Due Diligence',
    description: 'Review and verify loan documents for due diligence',
    landingPageTitle: 'Due Diligence Jobs',
    createJobButtonLabel: 'Create Due Diligence Job',
    stepLabels: {
      documentProcessing: 'Document Processing',
      documentVerification: 'Document Verification',
      investorGuidelineReview: 'Investor Guideline Review'
    }
  },
  [JobType.BA]: {
    jobType: JobType.BA,
    displayName: 'Borrower Analysis',
    description: 'Analyze borrower documents for underwriting',
    landingPageTitle: 'Borrower Analysis Jobs',
    createJobButtonLabel: 'Create Borrower Analysis Job',
    stepLabels: {
      documentProcessing: 'Document Processing',
      documentVerification: 'Document Verification',
      investorGuidelineReview: 'Underwriting Guideline Review'
    }
  },
  [JobType.TR]: {
    jobType: JobType.TR,
    displayName: 'Title Search',
    description: 'Automate property tax and recorded-document searches for a property',
    landingPageTitle: 'Title Search Jobs',
    createJobButtonLabel: 'Create Title Search Job',
    // The three legacy step labels are retained for compatibility with the
    // shared DD/BA step model; the title-search workflow's own labels live in
    // `titleSearchStepLabels` below.
    stepLabels: {
      documentProcessing: 'Document Processing',
      documentVerification: 'Document Review',
      investorGuidelineReview: 'Findings Display'
    },
    titleSearchStepLabels: {
      propertyTaxSearch: 'Property Tax Search',
      recordedDocumentSearch: 'Recorded Document Search',
      documentProcessing: 'Document Processing',
      findingsDisplay: 'Findings Display'
    }
  }
};

// ============================================
// Loan, File, Document Types
// ============================================

export enum LoanStatus {
  CREATED = 'CREATED',
  PROCESSING = 'PROCESSING',
  REVIEW_REQUIRED = 'REVIEW_REQUIRED',
  REVIEW_COMPLETED = 'REVIEW_COMPLETED',
}

export enum FileStatus {
  CREATED = 'CREATED',
  PROCESSING = 'PROCESSING',
  REVIEW_REQUIRED = 'REVIEW_REQUIRED',
  REVIEW_COMPLETED = 'REVIEW_COMPLETED',
}

export interface Loan {
  loanId: string;
  jobId: string;
  tenantId: string;
  username: string;
  cognitoSub: string;
  loanNumber?: string;
  borrowerName?: string;
  propertyAddress?: string;
  loanAmount?: string;
  coBorrowerNames?: string[];
  fileCount: number;
  status: LoanStatus;
  createdAt: string;
  startedAt?: string;
  completedAt?: string;
  processedFileCount: number;
  failedFileCount: number;
}

export interface File {
  fileId: string;
  loanId: string;
  jobId: string;
  tenantId: string;
  username: string;
  cognitoSub: string;
  filename: string;
  fileSize: number;
  s3Key: string;
  status: FileStatus;
  pageCount?: number;
  splitDocumentCount: number;
  invocationId?: string;
  createdAt: string;
  processedAt?: string;
  errorMessage?: string;
}

export interface Document {
  documentId: string;
  fileId: string;
  loanId: string;
  jobId: string;
  tenantId: string;
  username: string;
  cognitoSub: string;
  sourceFilename: string;
  pageRange: string;
  documentType: string;
  confidenceScore: number;
  requiresHumanReview: boolean;
  processedDataS3Key?: string;
  createdAt: string;
  processedAt?: string;
  reviewTaskARN?: string;
  errorMessage?: string;
  extractedFields?: string;
}

// ============================================
// Upload Credentials
// ============================================

export interface UploadCredentials {
  accessKeyId: string;
  secretAccessKey: string;
  sessionToken: string;
  expiresIn: number;
}

// ============================================
// API Response Types
// ============================================

export interface CreateJobResponse {
  job: Job;
  uploadCredentials: UploadCredentials;
  uploadBucket: string;
  uploadPrefix: string;
}

export interface CreateLoanResponse {
  loan: Loan;
  uploadCredentials: UploadCredentials;
  uploadBucket: string;
  uploadPrefix: string;
}

export interface JobSubmissionResponse {
  jobId: string;
  status: JobStatus;
  s3Uri: string;
  message: string;
}

export interface DeleteJobResponse {
  jobId: string;
  message: string;
}

export interface DeleteDocumentResponse {
  documentId: string;
  fileId: string;
  loanId: string;
  jobId: string;
  message: string;
}

// ============================================
// API Input Types
// ============================================

export interface CreateJobInput {
  name?: string;
  jobType: JobType;
  loanNumber?: string;
  borrowerName?: string;
  propertyAddress?: string;
  loanAmount?: string;
  coBorrowerNames?: string[];
  // Title Search (jobType === 'TR') fields. Optional on the shared input so
  // DD/BA payloads are unaffected; populated only for TR jobs (Requirement 1.3).
  clientName?: string;
  effectiveDate?: string; // ISO 8601 (AWSDate) calendar date
  propertyCounty?: string;
  propertyState?: string;
  // Title Analysis optional fields (Requirements 7.4, 7.13)
  useCase?: string;
  vestingEntity?: string;
  guarantorNames?: string;
  subjectLoanAmount?: number;
  noteDate?: string;
  searchStartDate?: string;
  indexesSearched?: string[];
  subjectMortgageRecordingReference?: string;
  expectedLienPosition?: string;
  currentLoanHolder?: string;
  apn?: string;
}

// Backward compatibility alias
export type CreateWorkflowJobInput = CreateJobInput;

export interface SubmitJobInput {
  jobId: string;
  isResubmission?: boolean;
}

export interface UpdateStepStatusInput {
  jobId: string;
  tenantId: string;
  jobType: JobType;
  step: StepType;
  status: StepStatus;
}

// Backward compatibility alias
export type UpdateWorkflowStepStatusInput = UpdateStepStatusInput;

export interface SubmitDocumentsForProcessingInput {
  jobId: string;
  tenantId: string;
  jobType: JobType;
}

export type FieldUpdateAction = 'ACCEPT' | 'EDIT' | 'REJECT';

export interface FieldUpdate {
  fieldId: string;
  value: string;
  action: FieldUpdateAction;
}

export interface SubmitVerificationInput {
  jobId: string;
  tenantId: string;
  jobType: JobType;
  documentId: string;
  fieldUpdates: FieldUpdate[];
}

export interface SubmitGuidelineAnalysisInput {
  jobId: string;
  tenantId: string;
  jobType: JobType;
  guidelineIds: string[];
}

export interface CreateLoanInput {
  loanId: string;
  jobId: string;
  tenantId: string;
  username: string;
  cognitoSub: string;
  loanNumber?: string;
  borrowerName?: string;
  propertyAddress?: string;
  loanAmount?: string;
}

export interface CreateFileInput {
  fileId: string;
  loanId: string;
  jobId: string;
  tenantId: string;
  username: string;
  cognitoSub: string;
  filename: string;
  fileSize: number;
  s3Key: string;
}

export interface UpdateFileInput {
  fileId: string;
  loanId: string;
  jobId: string;
  tenantId: string;
  status: FileStatus;
  pageCount?: number;
  splitDocumentCount?: number;
  processedAt?: string;
  errorMessage?: string;
}

export interface CreateDocumentInput {
  documentId: string;
  fileId: string;
  loanId: string;
  jobId: string;
  tenantId: string;
  username: string;
  cognitoSub: string;
  sourceFilename: string;
  pageRange: string;
}

export interface UpdateDocumentInput {
  documentId: string;
  fileId: string;
  loanId: string;
  jobId: string;
  tenantId: string;
  documentType: string;
  confidenceScore: number;
  requiresHumanReview: boolean;
  processedDataS3Key?: string;
  processedAt?: string;
  reviewTaskARN?: string;
  errorMessage?: string;
}

// ============================================
// Connection Types
// ============================================

export interface LoanConnection {
  items: Loan[];
  nextToken?: string;
}

export interface FileConnection {
  items: File[];
  nextToken?: string;
}

export interface DocumentConnection {
  items: Document[];
  nextToken?: string;
}

export interface DeleteFileResponse {
  fileId: string;
  loanId: string;
  jobId: string;
  message: string;
}

export interface DeleteDocumentResponse {
  documentId: string;
  fileId: string;
  loanId: string;
  jobId: string;
  message: string;
}

// ============================================
// Guideline Review Types
// ============================================

export type FindingStatus = 'PASS' | 'FAIL' | 'WARNING' | 'FAILED' | 'MISSING DATA';

export interface GuidelineRecord {
  tenantId: string;
  guidelineId: string;
  name: string;
  description: string;
  type?: string;
}

export interface LoanFinding {
  loanId: string;
  status: FindingStatus;
  explanation: string;
  unmetCriteria?: string[];
}

export interface GuidelineReviewResults {
  guidelineId: string;
  findings: LoanFinding[];
}

export interface DocumentVerificationResult {
  documentName: string;
  status: boolean;
  reason?: string;
}

export interface DocumentVerificationResults {
  jobId: string;
  tenantId: string;
  results: DocumentVerificationResult[];
}

export interface SubmitGuidelineReviewInput {
  jobId: string;
  tenantId: string;
  jobType: JobType;
  guidelineId: string;
}

export interface SaveModelConfigInput {
  modelId: string;
}

// ============================================
// Title Search Findings Types (jobType === 'TR')
// ============================================

/**
 * TaxInstallment - a single installment within a property tax record.
 * Monetary amounts may arrive as a number (GraphQL Float) or a string
 * (DynamoDB Decimal serialization); date fields are ISO 8601 (AWSDate) strings.
 */
export interface TaxInstallment {
  status?: string | null;
  estimated?: boolean | null;
  amount?: number | string | null;
  dueDate?: string | null;
  delinquentDate?: string | null;
  paidDate?: string | null;
}

/**
 * PropertyTaxInformation - structured property tax findings for a TR job.
 * Populated only when a property tax search returned results; otherwise the
 * containing field is null (see Requirement 6.10).
 */
export interface PropertyTaxInformation {
  taxSaleStatus?: string | null;
  taxYear?: number | null;
  taxType?: string | null;
  taxId?: string | null;
  taxingEntity?: string | null;
  totalAnnualTax?: number | string | null;
  paymentFrequency?: string | null;
  installments: TaxInstallment[];
  billYearNotes?: string | null;
  trueTaxAmount?: number | string | null;
}

/**
 * DeedRecord - a single recorded deed found for the property.
 * deedDate / recordedDate are ISO 8601 (AWSDate) strings; considerationAmount
 * may arrive as a number (GraphQL Float) or a string (DynamoDB Decimal).
 */
export interface DeedRecord {
  deedType?: string | null;
  grantee?: string | null;              // present title holder
  grantor?: string | null;              // from whom acquired
  deedDate?: string | null;
  recordedDate?: string | null;
  bookPageOrInstrument?: string | null;
  considerationAmount?: number | string | null;
}
